import React, { ReactElement, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { rgba } from 'polished';
import { Section } from '../Section';
import { ProjectSectionProps } from '../../model/ProjectSection';
import { Button } from '../buttons/Button';
import { Colors } from '../../constants/colors';
import { Fonts } from '../../constants/fonts';
import { Container } from '../Container';
import { Device } from '../../constants/device';
import { SendIcon } from '../icons/SendIcon';
import { CloseIcon } from '../icons/CloseIcon';
import { NewsletterIcon } from '../icons/NewsletterIcon';
import { OutlinedButton } from '../buttons/OutlinedButton';
import DOMPurify from 'dompurify';
import { useTranslation } from 'react-i18next';

// DOMPurify drops target by default, which would navigate a visitor away from
// a half-filled form. The links carry rel="noreferrer", so opening a new tab is safe.
const sanitize = (html: string) => DOMPurify.sanitize(html, { ADD_ATTR: ['target'] });

const NEWSLETTER_FORM = 'https://share-eu1.hsforms.com/1fwb93j0IRlW6y7PAgQgimQ2b49ti';

enum SubmissionStatus {
  Idle,
  Success,
  Error,
}

export type ServiceOption = {
  value: string;
  label: string;
};

type ContactProps = ProjectSectionProps & {
  /** Overrides the copy above the form, so a page can introduce it in its own words. */
  subtitleKey?: string;
  /** Distinguishes the leads of this page in HubSpot. */
  pageName?: string;
  /**
   * When given, the form asks which service the enquiry is about. The answer is
   * sent as the first line of the message, so no new HubSpot field is needed.
   */
  serviceOptions?: ServiceOption[];
  serviceLabel?: string;
  servicePlaceholder?: string;
  /** The page preselects the service when a visitor arrives from a section CTA. */
  service?: string;
  onServiceChange?: (service: string) => void;
};

export const Contact = (props: ContactProps): ReactElement | null => {
  const { t } = useTranslation();
  const portalId = '139607478';
  const formId = 'f7826b55-2334-4228-9eea-d269015b9656';

  const [formValues, setFormValues] = useState({
    email: '',
    name: '',
    company: '',
    message: '',
  });
  const [consentGiven, setConsentGiven] = useState(false);
  const [submissionStatus, setSubmissionStatus] = useState<SubmissionStatus>(SubmissionStatus.Idle);

  const selectedService = props.serviceOptions?.find((option) => option.value === props.service);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    setSubmissionStatus(SubmissionStatus.Idle);
    // The service is prepended to the message rather than sent as its own field,
    // which would have to be defined in the HubSpot form first.
    const message = selectedService ? `${props.serviceLabel}: ${selectedService.label}\n\n${formValues.message}` : formValues.message;
    try {
      const response = await fetch(`https://api.hsforms.com/submissions/v3/integration/submit/${portalId}/${formId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fields: [
            { name: 'email', value: formValues.email },
            { name: 'full_name_with_titles', value: formValues.name },
            { name: 'company', value: formValues.company },
            { name: 'message', value: message },
          ],
          context: {
            pageUri: window.location.href,
            pageName: props.pageName ?? 'slp.blue web',
          },
        }),
      });
      if (!response.ok) {
        throw new Error(`Failed to submit form: ${response.status}`);
      }

      setSubmissionStatus(SubmissionStatus.Success);
      setFormValues({
        email: '',
        name: '',
        company: '',
        message: '',
      });
      setConsentGiven(false);
      props.onServiceChange?.('');
    } catch (error) {
      console.error(error);
      setSubmissionStatus(SubmissionStatus.Error);
    }
  };

  const dismissSuccess = () => setSubmissionStatus(SubmissionStatus.Idle);

  const isFormValid = formValues.email.trim() !== '' && formValues.name.trim() !== '' && formValues.company.trim() !== '' && formValues.message.trim() !== '' && consentGiven;

  return (
    <Section id="contact" inverse={props.inverse} heading={t('contactForm.title')}>
      <Container>
        <ContactNote dangerouslySetInnerHTML={{ __html: sanitize(t(props.subtitleKey ?? 'contactForm.subtitle')) }} />
        <Newsletter>
          <span>{t('contactForm.newsletterNote')}</span>
          <OutlinedButton as="a" href={NEWSLETTER_FORM} target="_blank" rel="noopener noreferrer" maxWidth={'320px'}>
            <NewsletterIcon />
            {t('contactForm.newsletterButton')}
          </OutlinedButton>
        </Newsletter>
        <FormContainer>
          <FormHeader>
            <h3>{t('contactForm.header')}</h3>
            {submissionStatus === SubmissionStatus.Error && <ErrorShake>✘ {t('contactForm.error')}</ErrorShake>}
          </FormHeader>
          <ContactForm>
            <Input type={'text'} name={'name'} placeholder={t('contactForm.name')} value={formValues.name} onChange={handleChange} />
            <Input type={'email'} name={'email'} placeholder={t('contactForm.email')} value={formValues.email} onChange={handleChange} />
            <Input type={'text'} name={'company'} placeholder={t('contactForm.company')} value={formValues.company} onChange={handleChange} />
            {props.serviceOptions && (
              <ServiceField>
                <label htmlFor={'contact-service'}>{props.serviceLabel}</label>
                <Select id={'contact-service'} name={'service'} value={props.service ?? ''} onChange={(e) => props.onServiceChange?.(e.target.value)}>
                  <option value={''}>{props.servicePlaceholder}</option>
                  {props.serviceOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </ServiceField>
            )}
            <Textarea name={'message'} placeholder={t('contactForm.message')} value={formValues.message} onChange={handleChange} rows={4} />
          </ContactForm>
          <Consent>
            <input type={'checkbox'} id={'contact-consent'} checked={consentGiven} onChange={(e) => setConsentGiven(e.target.checked)} />
            <label htmlFor={'contact-consent'} dangerouslySetInnerHTML={{ __html: sanitize(t('contactForm.consent')) }} />
          </Consent>
          <FormFooter>
            <ResponseNote>{t('contactForm.responseNote')}</ResponseNote>
            <SubmitButtonWrapper>
              <Button onClick={handleSubmit} inverse disabled={!isFormValid}>
                {t('contactForm.submit')}
                <IconWrapper>
                  <SendIcon />
                </IconWrapper>
              </Button>
            </SubmitButtonWrapper>
          </FormFooter>
          {submissionStatus === SubmissionStatus.Success && (
            <SuccessOverlay>
              <CloseButton type={'button'} onClick={dismissSuccess} aria-label={t('contactForm.closeSuccess')}>
                <CloseIcon />
              </CloseButton>
              <SuccessTitle>{t('contactForm.success')}</SuccessTitle>
              <Button onClick={dismissSuccess} inverse maxWidth={'280px'}>
                {t('contactForm.sendAnother')}
              </Button>
            </SuccessOverlay>
          )}
        </FormContainer>
      </Container>
    </Section>
  );
};

const Newsletter = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 15px 30px;
  width: 100%;
  max-width: 1000px;
  margin: 10px auto 10px;
  text-align: center;

  a {
    width: auto;
    padding: 0 28px;
    text-decoration: none;
  }

  svg {
    width: 22px;
    height: 22px;
    fill: ${Colors.primary};
  }
`;

const FormContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 40px;
  border-radius: 20px;
  gap: 20px;
  background: ${Colors.primary};
  color: ${Colors.white};
  align-self: center;
  width: 100%;
  max-width: 1000px;
  margin-top: 20px;
`;

const ContactNote = styled.p`
  max-width: 1100px;
  align-self: center;
`;

const FormHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;

  /* The section already owns the h2, so the form title is an h3. Keep the
     sizing the browser gave the h1 it replaced. */
  h3 {
    font-size: 1.5em;
    font-weight: bold;
    margin: 0.83em 0;
  }

  ${Device.tablet} {
    flex-direction: column;
    text-align: center;
  }
`;

const SuccessOverlay = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 20px;
  /* Left translucent so the form stays visible behind the confirmation. */
  background: ${rgba(Colors.primary, 0.92)};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 25px;
  padding: 40px;
  text-align: center;
`;

const SuccessTitle = styled.div`
  font-family: ${Fonts.secondary};
  font-size: 28px;
  font-weight: 700;

  ${Device.tablet} {
    font-size: 22px;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  width: 32px;
  height: 32px;
  padding: 0;
  background: none;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 20px;
    height: 20px;
    stroke: none;
  }

  svg path {
    fill: ${Colors.white};
  }

  &:hover svg {
    opacity: 0.7;
  }

  &:focus-visible {
    outline: 2px solid ${Colors.white};
    outline-offset: 2px;
  }
`;

const FormFooter = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 20px;

  ${Device.tablet} {
    flex-direction: column;
    align-items: stretch;
  }
`;

const ResponseNote = styled.div`
  font-size: 14px;
  opacity: 0.85;
`;

const SubmitButtonWrapper = styled.div`
  ${Device.mobile} {
    width: 100%;
  }
`;

const ServiceField = styled.div`
  grid-column: 1 / span 3;
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 14px;

  ${Device.tablet} {
    grid-column: 1;
  }
`;

const Select = styled.select`
  padding: 10px;
  font: inherit;
  border: none;
  border-radius: 4px;
  min-width: 50px;

  &:focus-visible {
    outline: 2px solid ${Colors.white};
    outline-offset: 2px;
  }
`;

const Consent = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 14px;

  input {
    margin: 3px 0 0;
    width: 18px;
    height: 18px;
    flex: none;
    cursor: pointer;
  }

  label {
    cursor: pointer;
  }

  a {
    color: ${Colors.white};
  }
`;

const ContactForm = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  grid-template-rows: auto auto;
  grid-gap: 20px;

  & > textarea {
    grid-column: 1 / span 3;
  }

  ${Device.tablet} {
    grid-template-columns: 1fr;
    grid-template-rows: auto;

    & > textarea {
      grid-column: 1;
    }
  }
`;

const Input = styled.input`
  padding: 10px;
  font: inherit;
  border: none;
  outline: none;
  border-radius: 4px;
  min-width: 50px;
`;

const Textarea = styled.textarea`
  grid-column: 1 / span 3;
  padding: 10px;
  font: inherit;
  border: none;
  outline: none;
  border-radius: 4px;
  resize: none;
  min-width: 50px;

  ${Device.tablet} {
    grid-column: 1;
  }
`;

const IconWrapper = styled.div`
  height: 20px;
  display: flex;

  svg {
    height: 100%;
    width: auto;
    stroke: ${Colors.primary};
  }
`;

const shakeAnimation = keyframes`
  0% {
    transform: translateX(0);
  }
  20% {
    transform: translateX(-6px);
  }
  40% {
    transform: translateX(6px);
  }
  60% {
    transform: translateX(-6px);
  }
  80% {
    transform: translateX(6px);
  }
  100% {
    transform: translateX(0);
  }
`;

const ErrorShake = styled.span`
  animation: ${shakeAnimation} 0.4s ease-in-out;
`;
